/**
 * POS Incoming Orders Panel
 * Unified queue showing all incoming orders from different channels:
 * - Phone Orders (Vapi AI)
 * - Kiosk Orders (cash payments)
 * - WhatsApp/Chatbot Orders
 * - Online Menu Orders
 * 
 * Highly visible floating panel with sound alerts
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Phone, Monitor, MessageCircle, Globe, Bell, X, Check, 
  Clock, User, ShoppingBag, RefreshCw, Volume2, VolumeX,
  ChevronDown, ChevronUp, AlertCircle, DollarSign, Banknote,
  UtensilsCrossed, MapPin, Percent, Plus, Printer, Edit3, Pencil,
  Smartphone, QrCode, Send, Split, Trash2, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import OrderEditModal from './OrderEditModal';

// Channel configurations
const CHANNELS = {
  phone: {
    id: 'phone',
    name: 'Phone Orders',
    shortName: 'Phone',
    icon: Phone,
    color: 'purple',
    bgColor: 'bg-purple-500',
    textColor: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-purple-500',
    lightBg: 'bg-purple-50 dark:bg-purple-900/20',
    sources: ['vapi', 'phone']
  },
  kiosk: {
    id: 'kiosk',
    name: 'Kiosk Orders',
    shortName: 'Kiosk',
    icon: Monitor,
    color: 'cyan',
    bgColor: 'bg-cyan-500',
    textColor: 'text-cyan-600 dark:text-cyan-400',
    borderColor: 'border-cyan-500',
    lightBg: 'bg-cyan-50 dark:bg-cyan-900/20',
    sources: ['kiosk']
  },
  dinein: {
    id: 'dinein',
    name: 'Dine-In Tables',
    shortName: 'Dine-In',
    icon: UtensilsCrossed,
    color: 'orange',
    bgColor: 'bg-orange-500',
    textColor: 'text-orange-600 dark:text-orange-400',
    borderColor: 'border-orange-500',
    lightBg: 'bg-orange-50 dark:bg-orange-900/20',
    sources: ['dine-in', 'server']
  },
  whatsapp: {
    id: 'whatsapp',
    name: 'WhatsApp Orders',
    shortName: 'WhatsApp',
    icon: MessageCircle,
    color: 'green',
    bgColor: 'bg-green-500',
    textColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-green-500',
    lightBg: 'bg-green-50 dark:bg-green-900/20',
    sources: ['whatsapp', 'chatbot']
  },
  online: {
    id: 'online',
    name: 'Online Orders',
    shortName: 'Online',
    icon: Globe,
    color: 'blue',
    bgColor: 'bg-blue-500',
    textColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-500',
    lightBg: 'bg-blue-50 dark:bg-blue-900/20',
    sources: ['web', 'online', 'menu']
  }
};

export default function POSIncomingOrders({ staffId, onOrderAccepted }) {
  const [orders, setOrders] = useState({
    phone: [],
    kiosk: [],
    dinein: [],
    whatsapp: [],
    online: []
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null); // null = show all
  const [processingOrderId, setProcessingOrderId] = useState(null);
  
  // Cash Checkout Modal State
  const [cashCheckoutOrder, setCashCheckoutOrder] = useState(null);
  const [cashReceived, setCashReceived] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash'); // cash, eftpos, mpaisa_qr, split
  const [mpaisaRefCode, setMpaisaRefCode] = useState('');
  
  // Split Payment State
  const [splitPayments, setSplitPayments] = useState([]); // Array of { method, amount }
  const [splitAmount, setSplitAmount] = useState('');
  
  // EFTPOS Overseas Card Surcharge State
  const [showEftposCardTypeModal, setShowEftposCardTypeModal] = useState(false);
  const [eftposCardType, setEftposCardType] = useState(null); // 'local' or 'overseas'
  const [overseasSurchargeAmount, setOverseasSurchargeAmount] = useState(0);
  const OVERSEAS_SURCHARGE_PERCENT = 4; // 4% surcharge for overseas cards
  
  // Discount State
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountDollar, setDiscountDollar] = useState(0);
  const [discountMode, setDiscountMode] = useState('percent'); // 'percent' or 'dollar'
  const [orderDiscount, setOrderDiscount] = useState({ percent: 0, amount: 0 });
  
  // Print Bill State
  const [printingBill, setPrintingBill] = useState(false);
  
  // Edit Order Modal State
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingChannelConfig, setEditingChannelConfig] = useState(null);

  // Calculate total pending orders
  const totalOrders = Object.values(orders).reduce((sum, arr) => sum + arr.length, 0);
  
  // Helper to get order total
  const getOrderTotal = (order) => order?.total || 0;

  // Fetch all pending orders
  const fetchAllOrders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/pos/incoming-orders-queue', {
        headers: { 'X-Staff-ID': staffId }
      });
      
      const data = res.data;
      const newOrders = {
        phone: data.phone || [],
        kiosk: data.kiosk || [],
        dinein: data.dinein || [],
        whatsapp: data.whatsapp || [],
        online: data.online || []
      };
      
      // Check for new orders and play sound
      const prevTotal = Object.values(orders).reduce((sum, arr) => sum + arr.length, 0);
      const newTotal = Object.values(newOrders).reduce((sum, arr) => sum + arr.length, 0);
      
      if (newTotal > prevTotal && soundEnabled && prevTotal > 0) {
        playNotificationSound();
      }
      
      setOrders(newOrders);
      setLastFetch(new Date());
    } catch (err) {
      console.error('Failed to fetch pending orders:', err);
    } finally {
      setLoading(false);
    }
  }, [staffId, orders, soundEnabled]);

  // Poll for orders every 5 seconds
  useEffect(() => {
    fetchAllOrders();
    const interval = setInterval(fetchAllOrders, 5000);
    return () => clearInterval(interval);
  }, []);  // Only run once on mount, fetchAllOrders handles its own dependencies

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/sounds/order-notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Fallback: use Web Audio API beep
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gain.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => oscillator.stop(), 200);
      });
    } catch (e) {
      console.log('Sound not available');
    }
  };

  // Open Edit Order Modal
  const openEditModal = (order, channelKey) => {
    const config = CHANNELS[channelKey] || CHANNELS.kiosk;
    setEditingOrder(order);
    setEditingChannelConfig(config);
  };

  // Handle order updated from edit modal
  const handleOrderUpdated = () => {
    fetchAllOrders();
    toast.success('Order updated successfully');
  };

  // Accept/Process an order
  const handleAcceptOrder = async (order, channel) => {
    setProcessingOrderId(order.id);
    try {
      // Different endpoints based on channel
      let endpoint = '/api/orders/accept-incoming';
      if (channel === 'kiosk') {
        endpoint = `/api/orders/kiosk-mark-paid/${order.id}`;
      }
      
      const res = await api.post(endpoint, {
        order_id: order.id,
        channel: channel,
        staff_id: staffId,
        payment_method: order.payment_method || (channel === 'kiosk' ? 'cash' : 'prepaid')
      });
      
      if (res.data.success) {
        toast.success(`Order #${order.order_number || order.id?.slice(0, 8)} accepted!`);
        fetchAllOrders();
        if (onOrderAccepted) onOrderAccepted(order);
      }
    } catch (err) {
      toast.error('Failed to accept order: ' + (err.response?.data?.detail || err.message));
    } finally {
      setProcessingOrderId(null);
    }
  };

  // Open Cash Checkout Modal for Kiosk Orders
  const openCashCheckout = (order) => {
    openCashCheckoutWithDetails(order);
  };

  // Calculate change for cash checkout
  const calculateChange = () => {
    const received = parseFloat(cashReceived) || 0;
    const total = cashCheckoutOrder?.total || 0;
    return received - total;
  };

  // Calculate remaining amount for split payments
  const calculateSplitRemaining = () => {
    const total = cashCheckoutOrder?.total || 0;
    const paid = splitPayments.reduce((sum, p) => sum + p.amount, 0);
    return Math.max(0, total - paid);
  };

  // Add a split payment
  const addSplitPayment = (method, amount) => {
    const parsedAmount = parseFloat(amount) || 0;
    if (parsedAmount <= 0) {
      toast.error('Please enter an amount greater than 0');
      return;
    }
    const remaining = calculateSplitRemaining();
    const actualAmount = Math.min(parsedAmount, remaining);
    if (actualAmount <= 0) {
      toast.error('Payment already complete!');
      return;
    }
    setSplitPayments(prev => [...prev, { 
      method, 
      amount: actualAmount,
      timestamp: Date.now()
    }]);
    setSplitAmount('');
    const methodLabel = method === 'cash' ? 'Cash' : method === 'eftpos' ? 'EFTPOS' : 'M-PAiSA';
    toast.success(`Added ${methodLabel} $${actualAmount.toFixed(2)}`);
  };

  // Remove a split payment
  const removeSplitPayment = (index) => {
    setSplitPayments(prev => prev.filter((_, i) => i !== index));
  };

  // Reset split payment state without changing payment method
  const clearSplitState = () => {
    setSplitPayments([]);
    setSplitAmount('');
  };
  
  // Reset split payment and revert to cash
  const resetSplitPayment = () => {
    clearSplitState();
    setSelectedPaymentMethod('cash');
  };

  // Cancel/Void Order (for stuck orders from kiosk/server)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelAuthCode, setCancelAuthCode] = useState('');
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  
  // Manager Authorization Code for cancellations
  // Cancellation authorization is now validated on backend against staff PIN database

  const openCancelModal = (order) => {
    setCancellingOrder(order);
    setCancelReason('');
    setCancelAuthCode('');
    setShowCancelModal(true);
  };

  const cancelOrder = async () => {
    if (!cancellingOrder) return;
    if (!cancelReason.trim()) {
      toast.error('Please enter a reason for cancellation');
      return;
    }
    if (!cancelAuthCode || cancelAuthCode.length < 4) {
      toast.error('Please enter a valid manager/owner PIN code.');
      return;
    }
    
    setCancellingOrderId(cancellingOrder.id);
    try {
      const res = await api.post('/api/pos/cancel-incoming-order', {
        order_id: cancellingOrder.id,
        reason: cancelReason,
        staff_id: staffId,
        authorization_code: cancelAuthCode
      }, {
        headers: { 'X-Staff-ID': staffId }
      });
      
      if (res.data.success) {
        const orderLabel = cancellingOrder.order_type === 'dine-in' 
          ? `Table ${cancellingOrder.table_number}` 
          : `Order #${cancellingOrder.order_number || cancellingOrder.id?.slice(0, 8).toUpperCase()}`;
        toast.success(`${orderLabel} cancelled! ${res.data.authorized_by ? `Authorized by ${res.data.authorized_by}.` : ''} Owner notified.`);
        setShowCancelModal(false);
        setCancellingOrder(null);
        setCancelReason('');
        setCancelAuthCode('');
        fetchAllOrders();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      toast.error(errorMsg);
    } finally {
      setCancellingOrderId(null);
    }
  };

  // Process Payment for Kiosk or Dine-In Order (supports multiple payment methods including split)
  const processPayment = async () => {
    if (!cashCheckoutOrder) return;
    
    // Handle Split Payment
    if (selectedPaymentMethod === 'split') {
      const remaining = calculateSplitRemaining();
      if (remaining > 0.01) {  // Allow for small rounding errors
        toast.error(`Split payment incomplete! $${remaining.toFixed(2)} remaining`);
        return;
      }
      
      // Process split payment
      setProcessingOrderId(cashCheckoutOrder.id);
      try {
        const payload = {
          order_id: cashCheckoutOrder.id,
          payment_method: 'split',
          split_payments: splitPayments.map(p => ({
            method: p.method,
            amount: p.amount
          }))
        };
        
        const res = await api.post('/api/pos/incoming-order/complete-payment', payload, {
          headers: { 'X-Staff-ID': staffId }
        });
        
        if (res.data.success) {
          const orderLabel = cashCheckoutOrder.order_type === 'dine-in' 
            ? `Table ${cashCheckoutOrder.table_number}` 
            : `Order #${cashCheckoutOrder.order_number || cashCheckoutOrder.id?.slice(0, 8).toUpperCase()}`;
          
          toast.success(`${orderLabel} - Split Payment Complete!`);
          setCashCheckoutOrder(null);
          resetSplitPayment();
          setCashReceived('');
          setMpaisaRefCode('');
          fetchAllOrders();
          if (onOrderAccepted) onOrderAccepted(cashCheckoutOrder);
        }
      } catch (err) {
        toast.error('Failed to process split payment: ' + (err.response?.data?.detail || err.message));
      } finally {
        setProcessingOrderId(null);
      }
      return;
    }
    
    // Validate based on payment method (non-split)
    if (selectedPaymentMethod === 'cash') {
      const change = calculateChange();
      if (change < 0) {
        toast.error('Cash received is less than the order total!');
        return;
      }
    } else if (selectedPaymentMethod === 'mpaisa_qr') {
      if (!mpaisaRefCode || mpaisaRefCode.length < 6) {
        toast.error('Please enter a valid M-PAiSA reference code (min 6 characters)');
        return;
      }
    }
    // EFTPOS doesn't need validation - cashier confirms on terminal

    setProcessingOrderId(cashCheckoutOrder.id);
    try {
      // Calculate final total with any surcharge
      const orderTotal = getOrderTotal(cashCheckoutOrder);
      const finalTotal = orderTotal + (eftposCardType === 'overseas' ? overseasSurchargeAmount : 0);
      
      // Use the new complete-payment endpoint that supports all payment methods
      const payload = {
        order_id: cashCheckoutOrder.id,
        payment_method: selectedPaymentMethod,
        // EFTPOS overseas surcharge info
        eftpos_surcharge: eftposCardType === 'overseas' ? overseasSurchargeAmount : 0,
        eftpos_card_type: selectedPaymentMethod === 'eftpos' ? eftposCardType : null,
        final_total: finalTotal
      };
      
      if (selectedPaymentMethod === 'cash') {
        payload.cash_received = parseFloat(cashReceived);
        payload.change_given = calculateChange();
      } else if (selectedPaymentMethod === 'mpaisa_qr') {
        payload.mpaisa_ref = mpaisaRefCode;
      }
      // EFTPOS also sends surcharge info if overseas card
      
      const res = await api.post('/api/pos/incoming-order/complete-payment', payload, {
        headers: { 'X-Staff-ID': staffId }
      });
      
      if (res.data.success) {
        const orderLabel = cashCheckoutOrder.order_type === 'dine-in' 
          ? `Table ${cashCheckoutOrder.table_number}` 
          : `Order #${cashCheckoutOrder.order_number || cashCheckoutOrder.id?.slice(0, 8).toUpperCase()}`;
        
        let successMsg = `${orderLabel} paid!`;
        if (selectedPaymentMethod === 'cash' && res.data.change_given > 0) {
          successMsg += ` Change: $${res.data.change_given.toFixed(2)}`;
        } else if (selectedPaymentMethod === 'eftpos') {
          successMsg += ' EFTPOS payment confirmed!';
        } else if (selectedPaymentMethod === 'mpaisa_qr') {
          successMsg += ' M-PAiSA QR payment confirmed!';
        }
        
        toast.success(successMsg);
        setCashCheckoutOrder(null);
        setCashReceived('');
        setSelectedPaymentMethod('cash');
        setMpaisaRefCode('');
        setEftposCardType(null);
        setOverseasSurchargeAmount(0);
        resetSplitPayment();
        fetchAllOrders();
        if (onOrderAccepted) onOrderAccepted(cashCheckoutOrder);
      }
    } catch (err) {
      toast.error('Failed to process payment: ' + (err.response?.data?.detail || err.message));
    } finally {
      setProcessingOrderId(null);
    }
  };

  // Legacy function name for backwards compatibility
  const processCashPayment = processPayment;

  // Quick cash buttons
  const quickCashAmounts = [5, 10, 20, 50, 100];
  
  // Apply Discount to Order - supports both % and $
  const applyDiscount = async () => {
    if (!cashCheckoutOrder) return;
    
    // Validate that either percent or dollar is set
    const hasPercent = discountMode === 'percent' && discountPercent > 0;
    const hasDollar = discountMode === 'dollar' && discountDollar > 0;
    
    if (!hasPercent && !hasDollar) {
      toast.error('Please enter a discount amount');
      return;
    }
    
    try {
      const res = await api.post('/api/pos/order/apply-discount', {
        order_id: cashCheckoutOrder.id,
        discount_percent: discountMode === 'percent' ? discountPercent : 0,
        discount_dollar: discountMode === 'dollar' ? discountDollar : 0,
        discount_type: 'manual',
        staff_id: staffId
      }, {
        headers: { 'X-Staff-ID': staffId }
      });
      
      if (res.data.success) {
        // Update the local order with new totals
        setCashCheckoutOrder(prev => ({
          ...prev,
          discount_percent: res.data.discount_percent,
          discount_dollar: res.data.discount_dollar,
          discount_amount: res.data.discount_amount,
          total: res.data.new_total
        }));
        setOrderDiscount({
          percent: res.data.discount_percent,
          dollar: res.data.discount_dollar,
          amount: res.data.discount_amount
        });
        setShowDiscountPanel(false);
        toast.success(res.data.message);
        fetchAllOrders(); // Refresh the orders list
      }
    } catch (err) {
      toast.error('Failed to apply discount: ' + (err.response?.data?.detail || err.message));
    }
  };
  
  // Print Review Bill for Dine-In
  const printReviewBill = async () => {
    if (!cashCheckoutOrder) return;
    
    setPrintingBill(true);
    try {
      const res = await api.post('/api/pos/order/print-review-bill', {
        order_id: cashCheckoutOrder.id,
        staff_id: staffId
      }, {
        headers: { 'X-Staff-ID': staffId }
      });
      
      if (res.data.success) {
        toast.success(`Review bill sent to printer for Table ${res.data.table_number}`);
      }
    } catch (err) {
      toast.error('Failed to print bill: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPrintingBill(false);
    }
  };
  
  // Open checkout modal with discount info
  const openCashCheckoutWithDetails = (order) => {
    setCashCheckoutOrder(order);
    setCashReceived('');
    setDiscountPercent(order.discount_percent || 0);
    setOrderDiscount({
      percent: order.discount_percent || 0,
      amount: order.discount_amount || 0
    });
    setShowDiscountPanel(false);
  };

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const mins = Math.floor((now - date) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  };

  // Filter orders based on selected channel
  const getVisibleOrders = () => {
    if (selectedChannel) {
      return { [selectedChannel]: orders[selectedChannel] };
    }
    return orders;
  };

  const visibleOrders = getVisibleOrders();
  const hasAnyOrders = totalOrders > 0;

  return (
    <>
      {/* Floating Badge - Always Visible */}
      <div 
        className={`fixed top-4 right-4 z-50 transition-all duration-300 ${isExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      >
        <Button
          onClick={() => setIsExpanded(true)}
          className={`relative rounded-full shadow-lg ${hasAnyOrders ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-gray-700 hover:bg-gray-600'} text-white px-4 py-3`}
          data-testid="incoming-orders-badge"
        >
          <Bell className="w-5 h-5 mr-2" />
          <span className="font-bold">{totalOrders}</span>
          {hasAnyOrders && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full animate-ping" />
          )}
        </Button>
      </div>

      {/* Expanded Panel */}
      {isExpanded && (
        <div 
          className="fixed top-4 right-4 z-50 w-[420px] max-h-[85vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col"
          data-testid="incoming-orders-panel"
        >
          {/* Header */}
          <div className={`${hasAnyOrders ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-gray-600 to-gray-700'} text-white p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${hasAnyOrders ? 'bg-white/20 animate-pulse' : 'bg-white/10'} flex items-center justify-center`}>
                  <Bell className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Incoming Orders</h2>
                  <p className="text-sm text-white/80">
                    {totalOrders === 0 ? 'No pending orders' : `${totalOrders} order${totalOrders > 1 ? 's' : ''} waiting`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="text-white hover:bg-white/20 p-2"
                  title={soundEnabled ? 'Mute notifications' : 'Enable notifications'}
                >
                  {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchAllOrders}
                  className={`text-white hover:bg-white/20 p-2 ${loading ? 'animate-spin' : ''}`}
                  disabled={loading}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(false)}
                  className="text-white hover:bg-white/20 p-2"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Channel Tabs */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setSelectedChannel(null)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  selectedChannel === null 
                    ? 'bg-white text-gray-900' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                All ({totalOrders})
              </button>
              {Object.entries(CHANNELS).map(([key, config]) => {
                const count = orders[key]?.length || 0;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedChannel(key)}
                    className={`flex items-center gap-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      selectedChannel === key 
                        ? 'bg-white text-gray-900' 
                        : 'bg-white/20 text-white hover:bg-white/30'
                    } ${count > 0 ? 'ring-2 ring-yellow-400' : ''}`}
                    title={config.name}
                  >
                    <config.icon className="w-4 h-4" />
                    {count > 0 && <span className="font-bold">{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Orders List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {totalOrders === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Bell className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium">All caught up!</p>
                <p className="text-sm">New orders will appear here</p>
                <p className="text-xs mt-2 text-gray-400">Last checked: {lastFetch?.toLocaleTimeString() || 'Never'}</p>
              </div>
            ) : (
              Object.entries(visibleOrders).map(([channelKey, channelOrders]) => {
                if (channelOrders.length === 0) return null;
                const config = CHANNELS[channelKey];
                
                return (
                  <div key={channelKey} className="space-y-2">
                    {/* Channel Header (only show if viewing all) */}
                    {!selectedChannel && (
                      <div className={`flex items-center gap-2 px-2 py-1 ${config.lightBg} rounded-lg`}>
                        <config.icon className={`w-4 h-4 ${config.textColor}`} />
                        <span className={`text-sm font-semibold ${config.textColor}`}>
                          {config.name} ({channelOrders.length})
                        </span>
                      </div>
                    )}
                    
                    {/* Orders */}
                    {channelOrders.map((order) => (
                      <div
                        key={order.id}
                        className={`bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border-l-4 ${config.borderColor} shadow-sm hover:shadow-md transition-shadow`}
                        data-testid={`order-card-${order.id}`}
                      >
                        {/* Order Header */}
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xl font-bold ${config.textColor}`}>
                                #{order.order_number || order.id?.slice(0, 8).toUpperCase()}
                              </span>
                              <span className={`${config.bgColor} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>
                                {config.shortName}
                              </span>
                              {order.payment_status === 'paid' && (
                                <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs px-2 py-0.5 rounded-full">
                                  PAID
                                </span>
                              )}
                            </div>
                            {/* TABLE NUMBER - Prominent display for dine-in orders */}
                            {order.table_number && (
                              <div className="mt-1 inline-flex items-center gap-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-3 py-1 rounded-lg font-bold">
                                <MapPin className="w-4 h-4" />
                                TABLE {order.table_number}
                              </div>
                            )}
                            {/* Order type label for takeaway */}
                            {order.order_type === 'takeaway' && !order.table_number && (
                              <div className="mt-1 inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-3 py-1 rounded-lg font-bold">
                                <ShoppingBag className="w-4 h-4" />
                                TAKE AWAY
                              </div>
                            )}
                            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 dark:text-gray-400">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {order.customer_name || 'Guest'}
                              </span>
                              {order.customer_phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {order.customer_phone}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-gray-900 dark:text-white">
                              ${order.total?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                              <Clock className="w-3 h-3" />
                              {formatTimeAgo(order.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Items Preview */}
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-3 flex items-start gap-2">
                          <ShoppingBag className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div>
                            {(order.items || []).slice(0, 3).map((item, idx) => (
                              <span key={idx}>
                                {idx > 0 && ', '}
                                {item.quantity || 1}x {item.name}
                              </span>
                            ))}
                            {(order.items || []).length > 3 && (
                              <span className="text-gray-400"> +{order.items.length - 3} more</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Special Instructions */}
                        {order.special_instructions && (
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 text-sm p-2 rounded-lg mb-3 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{order.special_instructions}</span>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          {/* Edit Button */}
                          <Button
                            onClick={() => openEditModal(order, channelKey)}
                            variant="outline"
                            className="flex-shrink-0 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                            data-testid={`edit-order-${order.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          
                          {/* Accept/Checkout Button */}
                          <Button
                            onClick={() => (channelKey === 'kiosk' || channelKey === 'dinein') ? openCashCheckout(order) : handleAcceptOrder(order, channelKey)}
                            disabled={processingOrderId === order.id}
                            className={`flex-1 ${config.bgColor} hover:opacity-90 text-white py-3 font-semibold`}
                            data-testid={`accept-order-${order.id}`}
                          >
                            {processingOrderId === order.id ? (
                              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                            ) : (channelKey === 'kiosk' || channelKey === 'dinein') ? (
                              <DollarSign className="w-5 h-5 mr-2" />
                            ) : (
                              <Check className="w-5 h-5 mr-2" />
                            )}
                            {channelKey === 'kiosk' ? 'Cash Checkout' : channelKey === 'dinein' ? 'Collect Payment' : 'Accept Order'}
                          </Button>
                          
                          {/* Cancel/Void Order Button */}
                          <Button
                            onClick={() => openCancelModal(order)}
                            disabled={cancellingOrderId === order.id}
                            variant="outline"
                            className="px-3 py-3 border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                            data-testid={`cancel-order-${order.id}`}
                          >
                            <X className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
          
          {/* Footer */}
          {totalOrders > 0 && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                Orders auto-refresh every 5 seconds • {soundEnabled ? '🔔 Sound ON' : '🔕 Sound OFF'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Cash Checkout Modal for Kiosk & Dine-In Orders */}
      {cashCheckoutOrder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100]" data-testid="cash-checkout-modal">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className={`bg-gradient-to-r ${cashCheckoutOrder.order_type === 'dine-in' ? 'from-orange-500 to-orange-600' : 'from-cyan-500 to-cyan-600'} text-white p-5`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    {cashCheckoutOrder.order_type === 'dine-in' ? <UtensilsCrossed className="w-6 h-6" /> : <Banknote className="w-6 h-6" />}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {cashCheckoutOrder.order_type === 'dine-in' ? 'Table Payment' : 'Cash Checkout'}
                    </h2>
                    <p className={cashCheckoutOrder.order_type === 'dine-in' ? 'text-orange-100' : 'text-cyan-100'}>
                      {cashCheckoutOrder.order_type === 'dine-in' 
                        ? `Table ${cashCheckoutOrder.table_number || '?'} • Order #${cashCheckoutOrder.order_number || cashCheckoutOrder.id?.slice(0, 8).toUpperCase()}`
                        : `Kiosk Order #${cashCheckoutOrder.order_number || cashCheckoutOrder.id?.slice(0, 8).toUpperCase()}`
                      }
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setCashCheckoutOrder(null); setCashReceived(''); }}
                  className="text-white hover:bg-white/20 p-2 rounded-full"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-600 dark:text-gray-400">Customer</span>
                <span className="font-medium text-gray-900 dark:text-white">{cashCheckoutOrder.customer_name || 'Guest'}</span>
              </div>
              {cashCheckoutOrder.table_number && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-600 dark:text-gray-400">Table</span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">{cashCheckoutOrder.table_number}</span>
                </div>
              )}
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                <div className="flex items-start gap-2">
                  <ShoppingBag className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    {(cashCheckoutOrder.items || []).map((item, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{item.quantity || 1}x {item.name}</span>
                        <span className="ml-4">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Subtotal and Discount */}
              <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-700 dark:text-gray-300">${(cashCheckoutOrder.subtotal || cashCheckoutOrder.total)?.toFixed(2)}</span>
                </div>
                {(orderDiscount.percent > 0 || cashCheckoutOrder.discount_percent > 0) && (
                  <div className="flex items-center justify-between text-sm text-green-600 dark:text-green-400">
                    <span>Discount ({orderDiscount.percent || cashCheckoutOrder.discount_percent}%)</span>
                    <span>-${(orderDiscount.amount || cashCheckoutOrder.discount_amount)?.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              {/* Order Total */}
              <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-lg font-bold text-gray-900 dark:text-white">Order Total</span>
                <span className={`text-2xl font-bold ${cashCheckoutOrder.order_type === 'dine-in' ? 'text-orange-600 dark:text-orange-400' : 'text-cyan-600 dark:text-cyan-400'}`}>
                  ${cashCheckoutOrder.total?.toFixed(2) || '0.00'}
                </span>
              </div>
              
              {/* Action Buttons: Edit Order, Discount, Print Bill */}
              <div className="flex gap-2 mt-4">
                {/* Edit Order Button - Opens full edit modal */}
                <button
                  type="button"
                  onClick={() => {
                    setCashCheckoutOrder(null);
                    openEditModal(cashCheckoutOrder, cashCheckoutOrder.source === 'server' ? 'dinein' : 'kiosk');
                  }}
                  className="flex-1 flex items-center justify-center gap-1 py-2 px-3 text-sm font-medium border-2 border-green-300 dark:border-green-600 rounded-lg text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all"
                  data-testid="edit-order-full-btn"
                >
                  <Pencil className="w-4 h-4" />
                  Edit Order
                </button>
                <button
                  type="button"
                  onClick={() => setShowDiscountPanel(!showDiscountPanel)}
                  className="flex-1 flex items-center justify-center gap-1 py-2 px-3 text-sm font-medium border-2 border-purple-300 dark:border-purple-600 rounded-lg text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
                  data-testid="apply-discount-btn"
                >
                  <Percent className="w-4 h-4" />
                  Discount
                </button>
                {cashCheckoutOrder.table_number && (
                  <button
                    type="button"
                    onClick={printReviewBill}
                    disabled={printingBill}
                    className="flex-1 flex items-center justify-center gap-1 py-2 px-3 text-sm font-medium border-2 border-blue-300 dark:border-blue-600 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all disabled:opacity-50"
                    data-testid="print-bill-btn"
                  >
                    {printingBill ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                    Print Bill
                  </button>
                )}
              </div>
              
              {/* Discount Panel */}
              {showDiscountPanel && (
                <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-purple-700 dark:text-purple-300">Apply Discount</p>
                    {/* Toggle between % and $ */}
                    <div className="flex bg-white dark:bg-gray-800 rounded-lg p-0.5 border border-purple-300 dark:border-purple-600">
                      <button
                        type="button"
                        onClick={() => { setDiscountMode('percent'); setDiscountDollar(0); }}
                        data-testid="discount-mode-percent"
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                          discountMode === 'percent' 
                            ? 'bg-purple-500 text-white' 
                            : 'text-purple-600 dark:text-purple-400 hover:bg-purple-100'
                        }`}
                      >
                        %
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDiscountMode('dollar'); setDiscountPercent(0); }}
                        data-testid="discount-mode-dollar"
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                          discountMode === 'dollar' 
                            ? 'bg-green-500 text-white' 
                            : 'text-green-600 dark:text-green-400 hover:bg-green-100'
                        }`}
                      >
                        $
                      </button>
                    </div>
                  </div>
                  
                  {/* Percentage Discount Options */}
                  {discountMode === 'percent' && (
                    <>
                      <div className="grid grid-cols-5 gap-1 mb-2">
                        {[5, 10, 15, 20, 50].map(pct => (
                          <button
                            key={pct}
                            type="button"
                            onClick={() => setDiscountPercent(pct)}
                            data-testid={`discount-percent-${pct}`}
                            className={`py-2 text-sm font-bold rounded-md transition-all ${
                              discountPercent === pct
                                ? 'bg-purple-500 text-white'
                                : 'bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-600 text-purple-600 dark:text-purple-400 hover:bg-purple-100'
                            }`}
                          >
                            {pct}%
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={discountPercent || ''}
                          onChange={(e) => setDiscountPercent(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                          data-testid="discount-percent-input"
                          className="flex-1 px-3 py-2 border border-purple-300 dark:border-purple-600 rounded-md text-center font-bold bg-white dark:bg-gray-800"
                          placeholder="Custom %"
                        />
                        <button
                          type="button"
                          onClick={applyDiscount}
                          disabled={discountPercent <= 0}
                          data-testid="apply-percent-discount"
                          className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Apply
                        </button>
                      </div>
                    </>
                  )}
                  
                  {/* Dollar Discount Options */}
                  {discountMode === 'dollar' && (
                    <>
                      <div className="grid grid-cols-5 gap-1 mb-2">
                        {[1, 2, 5, 10, 20].map(amt => (
                          <button
                            key={amt}
                            type="button"
                            onClick={() => setDiscountDollar(amt)}
                            data-testid={`discount-dollar-${amt}`}
                            className={`py-2 text-sm font-bold rounded-md transition-all ${
                              discountDollar === amt
                                ? 'bg-green-500 text-white'
                                : 'bg-white dark:bg-gray-800 border border-green-300 dark:border-green-600 text-green-600 dark:text-green-400 hover:bg-green-100'
                            }`}
                          >
                            ${amt}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={discountDollar || ''}
                          onChange={(e) => setDiscountDollar(Math.max(0, parseFloat(e.target.value) || 0))}
                          data-testid="discount-dollar-input"
                          className="flex-1 px-3 py-2 border border-green-300 dark:border-green-600 rounded-md text-center font-bold bg-white dark:bg-gray-800"
                          placeholder="Custom $"
                        />
                        <button
                          type="button"
                          onClick={applyDiscount}
                          disabled={discountDollar <= 0}
                          data-testid="apply-dollar-discount"
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Apply
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Payment Method Section */}
            <div className="p-4">
              {/* Payment Method Selector - Cash, EFTPOS, M-PAiSA QR */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-2 font-medium">Payment Method</p>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => { setSelectedPaymentMethod('cash'); resetSplitPayment(); }}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      selectedPaymentMethod === 'cash'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-green-300 text-gray-600 dark:text-gray-400'
                    }`}
                    data-testid="payment-method-cash"
                  >
                    <Banknote className="w-5 h-5 mb-1" />
                    <span className="text-xs font-semibold">Cash</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { 
                      setShowEftposCardTypeModal(true);
                      clearSplitState(); 
                    }}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      selectedPaymentMethod === 'eftpos'
                        ? eftposCardType === 'overseas' 
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                          : 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 text-gray-600 dark:text-gray-400'
                    }`}
                    data-testid="payment-method-eftpos"
                  >
                    <CreditCard className="w-5 h-5 mb-1" />
                    <span className="text-xs font-semibold">EFTPOS</span>
                    {selectedPaymentMethod === 'eftpos' && eftposCardType && (
                      <span className={`text-[10px] ${eftposCardType === 'overseas' ? 'text-amber-600' : 'text-green-600'}`}>
                        {eftposCardType === 'overseas' ? '🌏 +4%' : '🇫🇯'}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSelectedPaymentMethod('mpaisa_qr'); clearSplitState(); }}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      selectedPaymentMethod === 'mpaisa_qr'
                        ? 'border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-red-300 text-gray-600 dark:text-gray-400'
                    }`}
                    data-testid="payment-method-mpaisa-qr"
                  >
                    <QrCode className="w-5 h-5 mb-1" />
                    <span className="text-xs font-semibold">M-PAiSA</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod('split')}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      selectedPaymentMethod === 'split'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-300 text-gray-600 dark:text-gray-400'
                    }`}
                    data-testid="payment-method-split"
                  >
                    <Split className="w-5 h-5 mb-1" />
                    <span className="text-xs font-semibold">Split</span>
                  </button>
                </div>
              </div>

              {/* Cash Payment Controls - Only show when Cash is selected */}
              {selectedPaymentMethod === 'cash' && (
                <>
                  {/* Amount Input Display */}
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 uppercase mb-1">Amount Received</p>
                    <div className={`border-2 rounded-lg p-3 text-center transition-all ${
                      cashReceived && calculateChange() >= 0 
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30' 
                        : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
                    }`}>
                      <span className="text-3xl font-bold text-gray-900 dark:text-white">
                        ${cashReceived || '0.00'}
                      </span>
                    </div>
                  </div>

                  {/* Quick Amount Buttons */}
                  <div className="grid grid-cols-5 gap-1 mb-2">
                    {[5, 10, 20, 50, 100].map(amount => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => setCashReceived(amount.toString())}
                        className="h-9 text-sm font-semibold border-2 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 active:scale-95 transition-all"
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>

                  {/* Exact Amount Button */}
                  <button
                    type="button"
                    onClick={() => setCashReceived(cashCheckoutOrder.total?.toFixed(2) || '0')}
                    className="w-full h-9 text-sm font-semibold border-2 border-cyan-500 rounded-md bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 mb-2 flex items-center justify-center gap-1 hover:bg-cyan-100 active:scale-98"
                  >
                    Exact (${cashCheckoutOrder.total?.toFixed(2) || '0.00'})
                  </button>

                  {/* Numpad */}
                  <div className="grid grid-cols-3 gap-1 mb-3">
                    {['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '⌫'].map(key => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          if (key === '⌫') {
                            setCashReceived(prev => prev.slice(0, -1));
                          } else if (key === '.') {
                            setCashReceived(prev => !prev.includes('.') ? prev + '.' : prev);
                          } else {
                            setCashReceived(prev => {
                              const parts = prev.split('.');
                              if (parts.length === 2 && parts[1].length >= 2) return prev;
                              return prev + key;
                            });
                          }
                        }}
                        className={`h-11 text-xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 flex items-center justify-center active:scale-95 active:bg-gray-100 transition-all ${
                          key === '⌫' ? 'text-red-500' : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>

                  {/* Change Display */}
                  {cashReceived && parseFloat(cashReceived) > 0 && (
                    <div className={`p-3 rounded-lg mb-3 text-center ${
                      calculateChange() >= 0 
                        ? 'bg-green-100 dark:bg-green-900/40 border-2 border-green-500' 
                        : 'bg-red-100 dark:bg-red-900/40 border-2 border-red-500'
                    }`}>
                      <p className={`text-xs uppercase mb-1 ${
                        calculateChange() >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                      }`}>
                        {calculateChange() >= 0 ? 'Change Due' : 'Amount Short'}
                      </p>
                      <p className={`text-3xl font-bold ${
                        calculateChange() >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`} data-testid="change-amount">
                        ${Math.abs(calculateChange()).toFixed(2)}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* EFTPOS - Show when EFTPOS is selected */}
              {selectedPaymentMethod === 'eftpos' && (
                <div className={`mb-4 p-4 rounded-lg border ${
                  eftposCardType === 'overseas' 
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      eftposCardType === 'overseas' 
                        ? 'bg-amber-100 dark:bg-amber-800'
                        : 'bg-blue-100 dark:bg-blue-800'
                    }`}>
                      <CreditCard className={`w-6 h-6 ${
                        eftposCardType === 'overseas' 
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-blue-600 dark:text-blue-400'
                      }`} />
                    </div>
                    <div>
                      <h4 className={`font-semibold ${
                        eftposCardType === 'overseas' 
                          ? 'text-amber-700 dark:text-amber-300'
                          : 'text-blue-700 dark:text-blue-300'
                      }`}>
                        {eftposCardType === 'overseas' ? '🌏 Overseas Card' : '🇫🇯 Local Card'} - EFTPOS Payment
                      </h4>
                      <p className={`text-sm ${
                        eftposCardType === 'overseas' 
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-blue-600 dark:text-blue-400'
                      }`}>Process payment on ANZ terminal</p>
                    </div>
                  </div>
                  {eftposCardType === 'overseas' && overseasSurchargeAmount > 0 && (
                    <div className="bg-amber-100 dark:bg-amber-900/40 rounded-lg p-3 mb-3 text-center">
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        Original: ${getOrderTotal(cashCheckoutOrder).toFixed(2)} + 4% Surcharge: ${overseasSurchargeAmount.toFixed(2)}
                      </p>
                      <p className="text-lg font-bold text-amber-800 dark:text-amber-200">
                        Total: ${(getOrderTotal(cashCheckoutOrder) + overseasSurchargeAmount).toFixed(2)}
                      </p>
                    </div>
                  )}
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <p>1. Enter <span className="font-bold">${((getOrderTotal(cashCheckoutOrder) || 0) + (eftposCardType === 'overseas' ? overseasSurchargeAmount : 0)).toFixed(2)}</span> on the terminal</p>
                    <p>2. Customer taps/inserts card</p>
                    <p>3. Click confirm below when approved</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowEftposCardTypeModal(true)}
                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mt-2 underline"
                  >
                    Change card type
                  </button>
                </div>
              )}

              {/* M-PAiSA QR - Show when M-PAiSA QR is selected */}
              {selectedPaymentMethod === 'mpaisa_qr' && (
                <div className="mb-4">
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 mb-3">
                    <div className="flex justify-center mb-3">
                      <img 
                        src="https://customer-assets-lxgj4vgw.emergentagent.net/job_bulaccino-live/artifacts/a2hvvxc3_Bula%20Cafe%20Namaka%20M-PAiSA%20QR-Code.png" 
                        alt="Bulaccino Cafe Namaka M-PAiSA QR Code"
                        className="w-40 h-40 rounded-lg shadow-md object-contain bg-white"
                      />
                    </div>
                    <p className="text-center text-sm text-red-600 dark:text-red-400 font-semibold">
                      Scan with M-PAiSA app to pay
                    </p>
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 uppercase mb-1">M-PAiSA Reference Code</p>
                    <input
                      type="text"
                      value={mpaisaRefCode}
                      onChange={(e) => setMpaisaRefCode(e.target.value.toUpperCase())}
                      placeholder="Enter reference code (min 6 chars)"
                      className="w-full px-4 py-3 border-2 border-red-300 dark:border-red-600 rounded-lg text-center font-mono text-lg bg-white dark:bg-gray-800 focus:border-red-500 focus:outline-none"
                      data-testid="mpaisa-ref-code-input"
                    />
                    {mpaisaRefCode && mpaisaRefCode.length < 6 && (
                      <p className="text-xs text-red-500 mt-1 text-center">Reference code must be at least 6 characters</p>
                    )}
                    {mpaisaRefCode && mpaisaRefCode.length >= 6 && (
                      <p className="text-xs text-green-500 mt-1 text-center flex items-center justify-center gap-1">
                        <Check className="w-3 h-3" /> Valid reference code
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Split Payment - Show when Split is selected */}
              {selectedPaymentMethod === 'split' && (
                <div className="mb-4 space-y-3">
                  {/* Running Total */}
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-purple-700 dark:text-purple-300">Order Total:</span>
                      <span className="font-bold text-purple-700 dark:text-purple-300">${(cashCheckoutOrder?.total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-purple-700 dark:text-purple-300">Paid:</span>
                      <span className="font-bold text-green-600">${splitPayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-1 pt-1 border-t border-purple-200 dark:border-purple-700">
                      <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Remaining:</span>
                      <span className={`font-bold ${calculateSplitRemaining() > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ${calculateSplitRemaining().toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Added Payments */}
                  {splitPayments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 uppercase font-medium">Payments Added</p>
                      {splitPayments.map((payment, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-2">
                            {payment.method === 'cash' ? (
                              <Banknote className="w-4 h-4 text-green-600" />
                            ) : payment.method === 'eftpos' ? (
                              <CreditCard className="w-4 h-4 text-blue-600" />
                            ) : (
                              <QrCode className="w-4 h-4 text-red-600" />
                            )}
                            <span className="text-sm font-medium capitalize">
                              {payment.method === 'mpaisa_qr' ? 'M-PAiSA' : payment.method === 'eftpos' ? 'EFTPOS' : payment.method}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-green-600">${payment.amount.toFixed(2)}</span>
                            <button
                              onClick={() => removeSplitPayment(idx)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Payment Section */}
                  {calculateSplitRemaining() > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 uppercase font-medium">Add Payment</p>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={splitAmount}
                          onChange={(e) => setSplitAmount(e.target.value)}
                          placeholder={`Max: $${calculateSplitRemaining().toFixed(2)}`}
                          className="flex-1 px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-center font-mono bg-white dark:bg-gray-800"
                          min="0"
                          step="0.01"
                          max={calculateSplitRemaining()}
                          data-testid="split-amount-input"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => addSplitPayment('cash', splitAmount || calculateSplitRemaining())}
                          className="flex items-center justify-center gap-2 p-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold"
                          data-testid="split-add-cash"
                        >
                          <Banknote className="w-5 h-5" />
                          Cash
                        </button>
                        <button
                          type="button"
                          onClick={() => addSplitPayment('eftpos', splitAmount || calculateSplitRemaining())}
                          className="flex items-center justify-center gap-2 p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold"
                          data-testid="split-add-eftpos"
                        >
                          <CreditCard className="w-5 h-5" />
                          EFTPOS
                        </button>
                        <button
                          type="button"
                          onClick={() => addSplitPayment('mpaisa_qr', splitAmount || calculateSplitRemaining())}
                          className="flex items-center justify-center gap-2 p-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold"
                          data-testid="split-add-mpaisa"
                        >
                          <QrCode className="w-5 h-5" />
                          M-PAiSA
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setSplitAmount((calculateSplitRemaining() / 2).toFixed(2))}
                          className="flex-1 py-2 text-xs font-medium border border-purple-300 rounded-lg text-purple-600 hover:bg-purple-50"
                        >
                          Half (${(calculateSplitRemaining() / 2).toFixed(2)})
                        </button>
                        <button
                          type="button"
                          onClick={() => setSplitAmount(calculateSplitRemaining().toFixed(2))}
                          className="flex-1 py-2 text-xs font-medium border border-purple-300 rounded-lg text-purple-600 hover:bg-purple-50"
                        >
                          Full (${calculateSplitRemaining().toFixed(2)})
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { 
                    setCashCheckoutOrder(null); 
                    setCashReceived(''); 
                    setSelectedPaymentMethod('cash');
                    setMpaisaRefCode('');
                    resetSplitPayment();
                  }}
                  className="h-11 text-sm font-semibold border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={processPayment}
                  disabled={
                    processingOrderId === cashCheckoutOrder.id ||
                    (selectedPaymentMethod === 'cash' && (!cashReceived || calculateChange() < 0)) ||
                    (selectedPaymentMethod === 'mpaisa_qr' && (!mpaisaRefCode || mpaisaRefCode.length < 6)) ||
                    (selectedPaymentMethod === 'split' && calculateSplitRemaining() > 0.01)
                    // EFTPOS has no extra validation - always enabled
                  }
                  className={`h-11 text-sm font-semibold rounded-lg text-white flex items-center justify-center gap-1 ${
                    processingOrderId !== cashCheckoutOrder.id &&
                    ((selectedPaymentMethod === 'cash' && cashReceived && calculateChange() >= 0) ||
                     selectedPaymentMethod === 'eftpos' ||
                     (selectedPaymentMethod === 'mpaisa_qr' && mpaisaRefCode && mpaisaRefCode.length >= 6) ||
                     (selectedPaymentMethod === 'split' && calculateSplitRemaining() <= 0.01))
                      ? selectedPaymentMethod === 'cash' 
                        ? 'bg-green-500 hover:bg-green-600 cursor-pointer'
                        : selectedPaymentMethod === 'eftpos'
                          ? 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                          : selectedPaymentMethod === 'split'
                            ? 'bg-purple-500 hover:bg-purple-600 cursor-pointer'
                            : 'bg-red-500 hover:bg-red-600 cursor-pointer'
                      : 'bg-gray-400 cursor-not-allowed opacity-70'
                  }`}
                  data-testid="complete-payment-btn"
                >
                  {processingOrderId === cashCheckoutOrder.id ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : selectedPaymentMethod === 'cash' ? (
                    <>
                      <Check className="w-4 h-4" />
                      Complete Sale
                    </>
                  ) : selectedPaymentMethod === 'eftpos' ? (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Confirm EFTPOS
                    </>
                  ) : selectedPaymentMethod === 'split' ? (
                    <>
                      <Split className="w-4 h-4" />
                      Complete Split Payment
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Confirm M-PAiSA
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Order Modal */}
      {editingOrder && (
        <OrderEditModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onOrderUpdated={handleOrderUpdated}
          staffId={staffId}
          channelConfig={editingChannelConfig}
        />
      )}
      
      {/* EFTPOS Card Type Modal - Local vs Overseas */}
      {showEftposCardTypeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[110]" onClick={() => setShowEftposCardTypeModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">EFTPOS Card Type</h3>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Is this a local or overseas card?</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Local Card Button */}
              <button
                onClick={() => {
                  setEftposCardType('local');
                  setOverseasSurchargeAmount(0);
                  setShowEftposCardTypeModal(false);
                  setSelectedPaymentMethod('eftpos');
                }}
                className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-green-500 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 transition-all"
                data-testid="eftpos-local-card-incoming"
              >
                <span className="text-3xl mb-2">🇫🇯</span>
                <span className="font-bold text-green-700 dark:text-green-400 text-lg">Local Card</span>
                <span className="text-sm text-green-600 dark:text-green-500 mt-1">No surcharge</span>
              </button>
              
              {/* Overseas Card Button */}
              <button
                onClick={() => {
                  const orderTotal = getOrderTotal(cashCheckoutOrder);
                  const surcharge = orderTotal * (OVERSEAS_SURCHARGE_PERCENT / 100);
                  setEftposCardType('overseas');
                  setOverseasSurchargeAmount(surcharge);
                  setShowEftposCardTypeModal(false);
                  setSelectedPaymentMethod('eftpos');
                }}
                className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-amber-500 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-all"
                data-testid="eftpos-overseas-card-incoming"
              >
                <span className="text-3xl mb-2">🌏</span>
                <span className="font-bold text-amber-700 dark:text-amber-400 text-lg">Overseas Card</span>
                <span className="text-sm text-amber-600 dark:text-amber-500 mt-1">+4% surcharge</span>
                {cashCheckoutOrder && (
                  <span className="text-xs text-amber-600 dark:text-amber-500 font-medium mt-1">
                    +${(getOrderTotal(cashCheckoutOrder) * (OVERSEAS_SURCHARGE_PERCENT / 100)).toFixed(2)}
                  </span>
                )}
              </button>
            </div>
            
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Current Total: <span className="font-bold">${getOrderTotal(cashCheckoutOrder).toFixed(2)}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Overseas cards incur a 4% bank surcharge
              </p>
            </div>
            
            <button
              onClick={() => setShowEftposCardTypeModal(false)}
              className="w-full mt-4 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Cancel/Void Order Modal */}
      {showCancelModal && cancellingOrder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100]" data-testid="cancel-order-modal">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <X className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Cancel Order</h2>
                    <p className="text-red-100">
                      {cancellingOrder.order_type === 'dine-in' 
                        ? `Table ${cancellingOrder.table_number || '?'} • Order #${cancellingOrder.order_number || cancellingOrder.id?.slice(0, 8).toUpperCase()}`
                        : `${cancellingOrder.source === 'kiosk' ? 'Kiosk' : 'Server'} Order #${cancellingOrder.order_number || cancellingOrder.id?.slice(0, 8).toUpperCase()}`
                      }
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowCancelModal(false); setCancellingOrder(null); setCancelAuthCode(''); }}
                  className="text-white hover:bg-white/20 p-2 rounded-full"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-5 space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <p className="text-red-800 dark:text-red-200 text-sm font-medium">
                  ⚠️ Manager authorization required. This action will be logged and reported to the owner.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Manager Authorization Code
                </label>
                <Input
                  type="password"
                  value={cancelAuthCode}
                  onChange={(e) => setCancelAuthCode(e.target.value)}
                  placeholder="Enter 6-digit authorization code"
                  maxLength={6}
                  className="w-full p-3 text-center text-2xl tracking-widest font-mono"
                  data-testid="cancel-auth-code-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cancellation Reason (Required)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g., Customer left, duplicate order, test order..."
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={3}
                  data-testid="cancel-reason-input"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => { setShowCancelModal(false); setCancellingOrder(null); setCancelAuthCode(''); }}
                  variant="outline"
                  className="flex-1 py-3"
                >
                  Go Back
                </Button>
                <Button
                  onClick={cancelOrder}
                  disabled={!cancelReason.trim() || cancelAuthCode.length !== 6 || cancellingOrderId === cancellingOrder.id}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-cancel-order-btn"
                >
                  {cancellingOrderId === cancellingOrder.id ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <X className="w-5 h-5 mr-2" />
                  )}
                  Cancel Order
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
